import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Snowflake,
  AlertTriangle,
  Phone,
  MapPin,
  Mail,
  CalendarClock,
  Layers,
  ListChecks,
  Image as ImageIcon,
  FileText,
  ChevronRight,
  ArrowRight,
  Sparkles,
} from "lucide-react";

function formatBytes(n) {
  if (n == null) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function Row({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-mist-line py-3 last:border-b-0 md:border-white/10">
      <div className="flex items-center gap-2.5">
        <span
          className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${
            accent ?? "bg-mist text-navy/70 md:bg-white/10 md:text-white/75"
          }`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-[13px] font-medium text-navy/65 md:text-white/70">
          {label}
        </span>
      </div>
      <span className="max-w-[60%] break-words text-right text-[13px] font-semibold text-navy md:text-white">
        {value || "\u2014"}
      </span>
    </div>
  );
}

/**
 * Demo confirmation card + Contractor Lead Summary Panel.
 *
 * Props:
 *   submission – the server response (or offline-fabricated equivalent).
 *                Must include all ServiceRequestOut fields and may have `_offline: true`.
 */
export default function RequestConfirmation({ submission }) {
  const headingRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      headingRef.current?.focus();
    });
  }, []);

  const isOffline = !!submission?._offline;
  const photoText = submission?.photo_filename
    ? `${submission.photo_filename}${
        submission.photo_size_bytes
          ? ` (${formatBytes(submission.photo_size_bytes)})`
          : ""
      }`
    : null;

  return (
    <div className="relative">
      {/* Soft section background */}
      <div className="absolute inset-0 bg-brand-gradient-soft" aria-hidden="true" />
      <div
        className="absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(circle at 12% 18%, rgba(34,211,238,0.16) 0%, transparent 48%), radial-gradient(circle at 88% 86%, rgba(245,158,11,0.10) 0%, transparent 55%)",
        }}
        aria-hidden="true"
      />

      <div className="container-page relative space-y-8 py-16 sm:py-20">
        {/* === Homeowner confirmation card === */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          data-testid="request-confirmation-card"
          className="relative overflow-hidden rounded-3xl border border-aqua/20 bg-brand-gradient p-8 text-white shadow-lift sm:p-12"
        >
          <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-aqua/30 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-16 -left-12 h-56 w-56 rounded-full bg-sun/20 blur-3xl" aria-hidden="true" />

          <div className="relative flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <span className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10 backdrop-blur">
                <Snowflake className="h-6 w-6 text-aqua" />
                <span className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-sun text-navy-deep shadow-sunGlow">
                  <CheckCircle2 className="h-3 w-3" />
                </span>
              </span>
              <div>
                <span className="pill-dark">
                  <Sparkles className="h-3 w-3 text-sun" />
                  {isOffline ? "Demo received (offline)" : "Demo received"}
                </span>
                <h2
                  ref={headingRef}
                  tabIndex={-1}
                  data-testid="request-confirmation-heading"
                  className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl"
                >
                  Demo request received.
                </h2>
              </div>
            </div>
          </div>

          <p
            data-testid="request-confirmation-disclaimer"
            className="relative mt-6 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg"
          >
            This sample form shows how an HVAC company could collect organized
            service details. Sun Chaser Cooling is not a real HVAC company and
            no emergency service request has been dispatched.
          </p>

          <div className="relative mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/"
              data-testid="request-confirmation-home"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white/95 px-5 py-3 text-sm font-semibold text-navy shadow-soft transition-colors hover:bg-white"
            >
              Back to home
            </Link>
            <Link
              to="/demo"
              data-testid="request-confirmation-demo"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-5 py-3 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02]"
            >
              See how this demo works
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            {isOffline && (
              <span
                data-testid="request-confirmation-offline-badge"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/[0.08] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/85 backdrop-blur"
              >
                <AlertTriangle className="h-3 w-3 text-sun" />
                Offline demo
              </span>
            )}
          </div>
        </motion.div>

        {/* === Contractor lead summary panel === */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut", delay: 0.08 }}
          data-testid="contractor-lead-panel"
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-navy text-white shadow-lift"
        >
          <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-aqua/15 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-24 -left-12 h-64 w-64 rounded-full bg-sun/10 blur-3xl" aria-hidden="true" />

          <div className="relative grid grid-cols-1 gap-0 lg:grid-cols-12">
            {/* Left column — meta + recommended next step */}
            <div className="border-b border-white/10 p-6 sm:p-8 lg:col-span-4 lg:border-b-0 lg:border-r">
              <div className="flex items-center justify-between gap-3">
                <span
                  data-testid="contractor-lead-panel-badge"
                  className="inline-flex items-center gap-1.5 rounded-full border border-sun/40 bg-sun/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sun"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sun" />
                  Demo Lead Preview
                </span>
              </div>
              <p className="mt-3 text-xs uppercase tracking-[0.16em] text-white/55">
                Live preview of what the contractor would see
              </p>

              <h3 className="mt-6 font-display text-2xl font-bold leading-tight tracking-tight text-white">
                Recommended next step
              </h3>
              <div className="mt-3 rounded-2xl border border-sun/30 bg-sun/15 p-4 backdrop-blur">
                <p
                  data-testid="contractor-lead-next-step"
                  className="font-display text-lg font-bold leading-snug text-sun"
                >
                  {submission?.recommended_next_step || "Callback to qualify"}
                </p>
              </div>

              <div className="mt-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55">
                  Pipeline status
                </p>
                <div
                  data-testid="contractor-lead-pipeline"
                  className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold"
                >
                  <span className="rounded-full bg-white/95 px-2.5 py-1 text-navy">
                    New Lead
                  </span>
                  <ChevronRight className="h-3 w-3 text-white/45" />
                  <span className="rounded-full bg-aqua/15 px-2.5 py-1 text-aqua">
                    Qualifying
                  </span>
                  <ChevronRight className="h-3 w-3 text-white/30" />
                  <span className="rounded-full border border-white/15 px-2.5 py-1 text-white/55">
                    Scheduled
                  </span>
                </div>
              </div>

              <p className="mt-8 text-[11px] leading-relaxed text-white/55">
                Demo data only &mdash; saved with{" "}
                <span className="font-mono text-aqua/90">is_demo: true</span>.
                Built by{" "}
                <a
                  href="https://prismforgedigital.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline-offset-2 hover:text-aqua hover:underline"
                >
                  PrismForge Digital
                </a>
                .
              </p>
            </div>

            {/* Right column — lead fields */}
            <div className="p-6 sm:p-8 lg:col-span-8">
              <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
                <div data-testid="contractor-lead-col-customer" className="rounded-2xl bg-white p-5 text-navy md:bg-transparent md:p-0 md:text-white">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/55 md:text-white/55">
                    Customer
                  </p>
                  <p
                    data-testid="contractor-lead-name"
                    className="mt-1 font-display text-xl font-bold leading-tight text-navy md:text-white"
                  >
                    {submission?.full_name || "—"}
                  </p>

                  <div className="mt-4 divide-y divide-mist-line rounded-xl border border-mist-line bg-white md:divide-white/10 md:border-white/10 md:bg-white/[0.04]">
                    <Row
                      icon={Phone}
                      label="Phone"
                      value={submission?.phone}
                      accent="bg-aqua/10 text-aqua-deep"
                    />
                    <Row
                      icon={Mail}
                      label="Email"
                      value={submission?.email}
                    />
                    <Row
                      icon={MapPin}
                      label="City"
                      value={submission?.city}
                    />
                    <Row
                      icon={ListChecks}
                      label="Preferred contact"
                      value={submission?.preferred_contact}
                    />
                  </div>
                </div>

                <div data-testid="contractor-lead-col-issue" className="mt-5 rounded-2xl bg-white p-5 text-navy md:mt-0 md:bg-transparent md:p-0 md:text-white">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/55 md:text-white/55">
                    Issue & system
                  </p>
                  <p
                    data-testid="contractor-lead-issue"
                    className="mt-1 font-display text-xl font-bold leading-tight text-navy md:text-white"
                  >
                    {submission?.issue_type || "—"}
                  </p>

                  <div className="mt-4 divide-y divide-mist-line rounded-xl border border-mist-line bg-white md:divide-white/10 md:border-white/10 md:bg-white/[0.04]">
                    <Row
                      icon={AlertTriangle}
                      label="Urgency"
                      value={submission?.urgency}
                      accent="bg-sun/15 text-sun"
                    />
                    <Row
                      icon={CalendarClock}
                      label="System age"
                      value={submission?.system_age}
                    />
                    <Row
                      icon={Snowflake}
                      label="Cooling status"
                      value={submission?.is_cooling_now}
                      accent="bg-aqua/15 text-aqua-deep"
                    />
                    <Row
                      icon={Layers}
                      label="Lead ID"
                      value={submission?.id ? submission.id.slice(0, 12) + "…" : "—"}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55">
                    <ImageIcon className="h-3 w-3" />
                    Photo
                  </div>
                  <p
                    data-testid="contractor-lead-photo"
                    className="mt-1 break-words text-sm text-white/85"
                  >
                    {photoText || "—"}
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55">
                    <FileText className="h-3 w-3" />
                    Notes
                  </div>
                  <p
                    data-testid="contractor-lead-notes"
                    className="mt-1 whitespace-pre-line text-sm text-white/85"
                  >
                    {submission?.notes?.trim() || "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
